import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RankInsigniaVertical } from './RankInsigniaVertical';
import { getRankKey } from './devPvpLadderMock';
import { BattleRankPortaledTooltip } from './BattleRankPortaledTooltip';
import './battlePlayerRankCorner.css';

function pvpRankNameTKey(rankKey) {
  if (!rankKey || typeof rankKey !== 'string') {
    throw new Error('battlePlayerRankCorner: rankKey is required for tooltip');
  }
  return `pvpRank.ranks.${rankKey}`;
}

/**
 * @param {number|null|undefined} playerRank - Honor / MMR leaderboard position (#n)
 * @param {{ pvp_ladder_rank_index?: number, pvp_in_league?: boolean }|null|undefined} playerPvpLadderRow - from /api/player-ranks
 */
export function resolveBattleRankCornerDisplay(playerRank, playerPvpLadderRow) {
  const rankNum = playerRank != null && playerRank !== undefined ? Number(playerRank) : null;
  const ladderRankIdx =
    playerPvpLadderRow != null && playerPvpLadderRow.pvp_ladder_rank_index != null
      ? Number(playerPvpLadderRow.pvp_ladder_rank_index)
      : null;
  const inLeague = playerPvpLadderRow?.pvp_in_league === true;

  const showMmrLeaderboardNumber =
    rankNum != null &&
    Number.isFinite(rankNum) &&
    (playerPvpLadderRow == null || inLeague === true || ladderRankIdx === 9);

  let ladderRankKey = null;
  if (
    playerPvpLadderRow != null &&
    ladderRankIdx != null &&
    Number.isFinite(ladderRankIdx) &&
    inLeague !== true &&
    ladderRankIdx !== 9
  ) {
    try {
      ladderRankKey = getRankKey(ladderRankIdx);
    } catch {
      ladderRankKey = null;
    }
  }

  return { showMmrLeaderboardNumber, ladderRankKey, rankNum, inLeague };
}

/**
 * Localized PvP rank label for battle HUD tooltips (insignia or General league badge).
 * @param {(key: string, opts?: object) => string} t - i18n translate function
 */
export function resolveBattleRankTooltipText(t, playerRank, playerPvpLadderRow) {
  if (typeof t !== 'function') {
    throw new Error('resolveBattleRankTooltipText: t is required');
  }
  const { showMmrLeaderboardNumber, ladderRankKey, rankNum, inLeague } = resolveBattleRankCornerDisplay(
    playerRank,
    playerPvpLadderRow
  );

  if (ladderRankKey) {
    return t(pvpRankNameTKey(ladderRankKey));
  }

  if (
    showMmrLeaderboardNumber &&
    rankNum != null &&
    playerPvpLadderRow != null &&
    (inLeague === true || playerPvpLadderRow.pvp_ladder_rank_index === 9)
  ) {
    return t(pvpRankNameTKey('general'));
  }

  return null;
}

/**
 * Player avatar corner: PvP ladder insignia before General; after reaching General (league), same # as before.
 * @param {boolean} [compact] - Smaller badge (opponent avatar scale)
 * @param {'top'|'bottom'} [tooltipPosition] - preferred portaled tooltip placement (default top)
 */
export function BattlePlayerRankCorner({
  playerRank,
  playerPvpLadderRow,
  getRankStyle,
  compact = false,
  tooltipPosition = 'top',
}) {
  const { t } = useTranslation();
  const anchorRef = useRef(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { showMmrLeaderboardNumber, ladderRankKey, rankNum } = resolveBattleRankCornerDisplay(
    playerRank,
    playerPvpLadderRow
  );
  const tooltipText = resolveBattleRankTooltipText(t, playerRank, playerPvpLadderRow);
  const preferBottom = tooltipPosition === 'bottom';

  const showTooltip = useCallback(() => {
    if (!tooltipText) return;
    setTooltipOpen(true);
  }, [tooltipText]);

  const hideTooltip = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handlePointerDown = useCallback(
    (event) => {
      if (!tooltipText) return;
      if (event.pointerType === 'mouse') return;
      event.stopPropagation();
      setTooltipOpen((open) => !open);
    },
    [tooltipText]
  );

  const tooltipHandlers = tooltipText
    ? {
        ref: anchorRef,
        onPointerEnter: showTooltip,
        onPointerLeave: hideTooltip,
        onPointerDown: handlePointerDown,
        'aria-label': tooltipText,
      }
    : {};

  const rootClass =
    'battle-player-rank-corner' +
    (compact ? ' battle-player-rank-corner--compact' : '') +
    (tooltipText ? ' battle-player-rank-corner--has-tooltip' : '');

  const portaledTooltip =
    tooltipText != null ? (
      <BattleRankPortaledTooltip
        anchorRef={anchorRef}
        text={tooltipText}
        preferBottom={preferBottom}
        open={tooltipOpen}
      />
    ) : null;

  if (ladderRankKey) {
    return (
      <>
        <div
          className={`${rootClass} battle-player-rank-corner--insignia battle-player-rank-corner--rank-${ladderRankKey}`}
          {...tooltipHandlers}
        >
          <div className="battle-player-rank-corner__insignia-scale" aria-hidden="true">
            <RankInsigniaVertical rankKey={ladderRankKey} />
          </div>
        </div>
        {portaledTooltip}
      </>
    );
  }

  if (showMmrLeaderboardNumber && rankNum != null) {
    return (
      <>
        <div
          className={`${rootClass} battle-player-rank-corner--number`}
          style={getRankStyle(rankNum)}
          {...tooltipHandlers}
        >
          #{rankNum}
        </div>
        {portaledTooltip}
      </>
    );
  }

  return null;
}
